import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { LogIn } from "lucide-react";

import { ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { PasswordInput } from "@/components/PasswordInput";
import { Spinner } from "@/components/Spinner";
import { ThemeToggle } from "@/components/ThemeToggle";

type FormValues = { username: string; password: string };

export function LoginPage() {
  const { me, loading, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormValues>({ defaultValues: { username: "", password: "" } });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner label="Checking session…" />
      </div>
    );
  }
  if (me) {
    const from = (loc.state as { from?: string } | null)?.from ?? "/admin";
    return <Navigate to={from} replace />;
  }

  async function onSubmit(values: FormValues) {
    setError(null);
    try {
      await login(values.username, values.password);
      const from = (loc.state as { from?: string } | null)?.from ?? "/admin";
      nav(from, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Login failed.");
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex justify-end px-6 py-4">
        <ThemeToggle />
      </header>
      <main className="flex-1 flex items-center justify-center px-6">
        <form onSubmit={handleSubmit(onSubmit)} className="card w-full max-w-sm p-8 space-y-6">
          <div className="text-center">
            <div className="inline-flex p-3 rounded-full bg-brand-green/20 text-brand-greenAlt mb-3">
              <LogIn size={22} />
            </div>
            <h1 className="text-2xl font-bold">Admin Portal Login</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Sign in to manage student tickets.
            </p>
          </div>

          {error && (
            <div className="alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium mb-1">
                Username
              </label>
              <input
                id="username"
                type="text"
                autoComplete="username"
                className="input-field"
                {...register("username", { required: true })}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1">
                Password
              </label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                {...register("password", { required: true })}
              />
            </div>
          </div>

          <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
            {isSubmitting ? <Spinner label="Signing in…" /> : "Login"}
          </button>
        </form>
      </main>
    </div>
  );
}
